/** SUNTRONIX ONE — ONE TODAY. Personal action-first workspace (/today). */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle, CalendarClock, CheckCircle2, ChevronDown, Clock,
  ExternalLink, HelpCircle, Loader2, Pin, StampIcon, UserPlus, Users,
  PhoneCall, Search, Bell,
} from "lucide-react";
import TodayWins from "@/components/app/TodayWins";
import QuickQuoteLauncher from "@/components/app/QuickQuoteLauncher";

const fmtINR = (n) => n == null ? null :
  `₹ ${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const PRIO_STYLE = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
};

export default function OneToday() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("me");
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState([]);
  const [pins, setPins] = useState([]);
  const [landing, setLanding] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);

  const load = useCallback(async (sc = scope, query = q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/one-today/board", { params: { scope: sc, q: query } });
      setBoard(data);
    } catch { toast.error("Could not load ONE TODAY"); }
    finally { setLoading(false); }
  }, [scope, q]);

  useEffect(() => {
    api.get("/one-command/recent").then(({ data }) => setRecent((data.items || []).slice(0, 6))).catch(() => {});
    api.get("/one-command/pins").then(({ data }) => setPins((data.items || []).slice(0, 8))).catch(() => {});
    api.get("/one-today/landing").then(({ data }) => setLanding(data.landing)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(scope, q), 250);
    return () => clearTimeout(t);
  }, [load, q, scope]);

  const doAction = async (action) => {
    if (action.kind === "route") { navigate(action.route); return; }
    try {
      await api.request({ method: action.api.method, url: action.api.url, data: action.api.body });
      toast.success("Done");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const doSnooze = async (item, until) => {
    const { data } = await api.post("/one-today/snooze", { key: item.key, until });
    toast.success(`Snoozed until ${data.until}`);
    load();
  };

  const doComplete = async (item) => {
    await api.post("/one-today/complete", { key: item.key });
    toast.success("Marked done");
    load();
  };

  const setLandingPref = async (checked) => {
    const val = checked ? "today" : "os";
    setLanding(val);
    window.__oneTodayLanding = val;
    await api.post("/one-today/landing", { landing: val });
    toast.success(checked ? "My Action Centre is now your landing page" : "OS Home is now your landing page");
  };

  const firstName = (user?.name || user?.email || "").split(/[ @]/)[0];
  const cardProps = { onAction: doAction, onSnooze: doSnooze, onComplete: doComplete, onAssign: setAssignTarget, showOwner: scope === "team" };

  return (
    <div className="max-w-[1200px] mx-auto px-4 py-5 space-y-6" data-testid="one-today-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-400">My Action Centre · {board?.date || ""}</div>
          <h1 className="text-xl font-semibold text-slate-900">Good day, {firstName}</h1>
          <p className="text-[12px] text-slate-500 mt-0.5">Request → Assign → Action → Track → Close</p>
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          {board?.is_management && (
            <div className="flex rounded-md border border-slate-200 overflow-hidden text-[12px]">
              <button data-testid="today-scope-me" onClick={() => setScope("me")}
                className={`px-3 py-1.5 ${scope === "me" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                My work
              </button>
              <button data-testid="today-scope-team" onClick={() => setScope("team")}
                className={`px-3 py-1.5 ${scope === "team" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                <Users className="h-3.5 w-3.5 inline mr-1" />My team
              </button>
            </div>
          )}
          <label className="flex items-center gap-2 text-[12px] text-slate-600" data-testid="today-landing-toggle">
            <Switch checked={landing === "today"} onCheckedChange={setLandingPref} />
            Land here at login
          </label>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search my work…"
          className="pl-9 h-9" data-testid="today-search-input" />
      </div>

      {["super_admin", "management", "sales_coordinator", "operations", "sales", "account_manager"].includes(user?.role) && (
        <QuickQuoteLauncher compact />
      )}

      <TodayWins team={!!board?.is_management} />

      {loading && !board ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-16 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Building your day…
        </div>
      ) : board && (
        <>
          <Section icon={AlertTriangle} title="Needs your attention" count={board.counts.attention} testId="today-attention">
            {board.attention.length === 0
              ? <EmptyNote text="Nothing needs your attention right now." />
              : <div className="grid gap-2.5">{board.attention.map((it) => <ActionCard key={it.key} item={it} {...cardProps} />)}</div>}
          </Section>

          <Section icon={Clock} title="Today" testId="today-due">
            {board.today.length === 0
              ? <EmptyNote text="No work due today." />
              : board.today.map((g) => (
                <div key={g.group} className="mb-3">
                  <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400 mb-1.5">{g.group}</div>
                  <div className="grid gap-2.5">{g.items.map((it) => <ActionCard key={`t-${it.key}`} item={it} compact tp="t-" {...cardProps} />)}</div>
                </div>
              ))}
          </Section>

          <div className="grid md:grid-cols-2 gap-6">
            <Section icon={StampIcon} title="Approvals" count={board.counts.approvals} testId="today-approvals">
              {board.approvals.length === 0
                ? <EmptyNote text="No approvals waiting." />
                : <div className="grid gap-2.5">{board.approvals.map((it) => <ActionCard key={it.key} item={it} compact tp="a-" {...cardProps} />)}</div>}
            </Section>
            <Section icon={PhoneCall} title="Follow-ups" count={board.counts.followups} testId="today-followups">
              {board.followups.length === 0
                ? <EmptyNote text="No active follow-up plans." />
                : <div className="grid gap-2.5">{board.followups.map((it) => <ActionCard key={`f-${it.key}`} item={it} compact tp="f-" {...cardProps} />)}</div>}
            </Section>
          </div>

          <Section icon={CalendarClock} title="Coming up" count={board.counts.coming_up} testId="today-coming-up">
            {board.coming_up.length === 0
              ? <EmptyNote text="Nothing scheduled ahead." />
              : <div className="grid sm:grid-cols-2 gap-2.5">{board.coming_up.map((it) => <ActionCard key={`c-${it.key}`} item={it} compact tp="c-" {...cardProps} />)}</div>}
          </Section>

          <div className="grid md:grid-cols-2 gap-6">
            <Section icon={Bell} title="Recently worked on" testId="today-recent">
              {recent.length === 0 ? <EmptyNote text="No recent records yet." /> : (
                <div className="grid gap-1.5">
                  {recent.map((r) => (
                    <button key={r.id} data-testid={`today-recent-${r.entity_id}`}
                      onClick={() => r.route && navigate(r.route)}
                      className="flex items-center gap-2 text-left rounded-md border border-slate-100 bg-white hover:bg-slate-50 px-3 py-2">
                      <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="text-sm text-slate-800 truncate">{r.title}</span>
                      {r.subtitle && <span className="text-xs text-slate-400 truncate ml-auto max-w-[140px]">{r.subtitle}</span>}
                    </button>
                  ))}
                </div>
              )}
            </Section>
            <Section icon={Pin} title="Pinned" testId="today-pinned">
              {pins.length === 0 ? <EmptyNote text="Pin records from Cmd+K to see them here." /> : (
                <div className="grid gap-1.5">
                  {pins.map((p) => (
                    <button key={p.id} data-testid={`today-pin-${p.entity_id}`}
                      onClick={() => p.route && navigate(p.route)}
                      className="flex items-center gap-2 text-left rounded-md border border-slate-100 bg-white hover:bg-slate-50 px-3 py-2">
                      <Pin className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                      <span className="text-sm text-slate-800 truncate">{p.title}</span>
                      {p.subtitle && <span className="text-xs text-slate-400 truncate ml-auto max-w-[140px]">{p.subtitle}</span>}
                    </button>
                  ))}
                </div>
              )}
            </Section>
          </div>

          {board.snoozed_count > 0 && (
            <div className="text-[12px] text-slate-400" data-testid="today-snoozed-note">
              {board.snoozed_count} item{board.snoozed_count === 1 ? "" : "s"} snoozed — they'll return on their snooze date.
            </div>
          )}
        </>
      )}

      <AssignDialog target={assignTarget} onClose={() => setAssignTarget(null)} onDone={() => { setAssignTarget(null); load(); }} />
    </div>
  );
}

const Section = ({ icon: Icon, title, count, children, testId }) => (
  <section data-testid={testId}>
    <div className="flex items-center gap-2 mb-2.5">
      <Icon className="h-4 w-4 text-slate-500" strokeWidth={1.8} />
      <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">{title}</h2>
      {count != null && count > 0 && (
        <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{count}</span>
      )}
    </div>
    {children}
  </section>
);

const EmptyNote = ({ text }) => (
  <div className="text-[13px] text-slate-400 border border-dashed border-slate-200 rounded-md px-3 py-3">{text}</div>
);

function ActionCard({ item, compact, onAction, onSnooze, onComplete, onAssign, showOwner, tp = "" }) {
  const [showWhy, setShowWhy] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const tid = `${tp}${item.key}`;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3.5 py-3 flex flex-col gap-1.5"
      data-testid={`today-card-${tid}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-900">{item.title}</span>
            {item.priority && (
              <span className={`text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 ${PRIO_STYLE[item.priority] || PRIO_STYLE.low}`}>
                {item.priority}
              </span>
            )}
            {item.value_inr != null && <span className="text-[11px] font-mono text-slate-500">{fmtINR(item.value_inr)}</span>}
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            {item.reason}
            {item.due_at && <span className="ml-2 text-slate-400">Due {item.due_at}</span>}
            {(item.responsible || (showOwner && item.owner)) && (
              <span className="ml-2 text-slate-400">· {item.assignee ? "Assigned to" : "Owner"} {item.responsible || item.owner}</span>
            )}
          </div>
        </div>
      </div>

      {showWhy && (
        <div className="text-[12px] text-slate-600 bg-slate-50 border border-slate-100 rounded px-2.5 py-2" data-testid={`today-why-${tid}`}>
          {item.why}
        </div>
      )}

      <div className="flex items-center gap-1.5 flex-wrap">
        {(item.actions || []).map((a, i) => (
          <button key={i} onClick={() => onAction(a)}
            data-testid={`today-action-${tid}-${i}`}
            className={`h-8 px-2.5 rounded text-[12px] font-medium inline-flex items-center gap-1 ${i === 0
              ? "bg-slate-900 text-white hover:bg-slate-800"
              : "border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            {a.kind === "route" ? <ExternalLink className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
            {a.label}
          </button>
        ))}
        {item.can_complete && (
          <button onClick={() => onComplete(item)} data-testid={`today-complete-${tid}`}
            className="h-8 px-2.5 rounded text-[12px] border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Done
          </button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`today-snooze-${tid}`}
              className="h-8 px-2 rounded text-[12px] border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> Snooze <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52">
            <DropdownMenuItem onClick={() => onSnooze(item, "today")} data-testid={`snooze-today-${tid}`}>Rest of today</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSnooze(item, "tomorrow")}>Until day after tomorrow</DropdownMenuItem>
            <div className="px-2 py-1.5 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <input type="date" value={customDate} onChange={(e) => setCustomDate(e.target.value)}
                className="border border-slate-200 rounded px-1.5 py-1 text-[12px] flex-1" />
              <button disabled={!customDate} onClick={() => customDate && onSnooze(item, customDate)}
                className="text-[12px] text-slate-700 border border-slate-200 rounded px-2 py-1 disabled:opacity-40">Set</button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <button onClick={() => onAssign(item)} data-testid={`today-assign-${tid}`}
          className="h-8 px-2 rounded text-[12px] border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center gap-1">
          <UserPlus className="h-3 w-3" /> {item.assignee ? "Reassign" : "Assign"}
        </button>
        <button onClick={() => setShowWhy((s) => !s)} data-testid={`today-whybtn-${tid}`}
          className="h-8 px-2 rounded text-[12px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 ml-auto">
          <HelpCircle className="h-3 w-3" /> Why?
        </button>
      </div>
    </div>
  );
}

function AssignDialog({ target, onClose, onDone }) {
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!target) return;
    api.get("/one-today/assignees").then(({ data }) => setUsers(data.items || [])).catch(() => {});
  }, [target]);
  const assign = async (email) => {
    setSaving(true);
    try {
      await api.post("/one-today/assign", { key: target.key, assignee_email: email, title: target.title });
      toast.success(`Assigned to ${email}`);
      onDone();
    } catch { toast.error("Could not assign"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="today-assign-dialog">
        <DialogHeader><DialogTitle className="text-base">Assign: {target?.title}</DialogTitle></DialogHeader>
        {target?.assign_history?.length > 0 && (
          <div className="text-[11px] text-slate-400 space-y-0.5">
            {target.assign_history.map((h, i) => (
              <div key={i}>→ {h.assignee_email} by {h.assigned_by} on {(h.at || "").slice(0, 10)}</div>
            ))}
          </div>
        )}
        <div className="max-h-64 overflow-y-auto grid gap-1">
          {users.map((u) => (
            <button key={u.email} disabled={saving} onClick={() => assign(u.email)}
              data-testid={`assignee-${u.email}`}
              className="flex items-center gap-2 text-left rounded-md border border-slate-100 hover:bg-slate-50 px-3 py-2">
              <span className="text-sm text-slate-800">{u.name || u.email}</span>
              <span className="text-[11px] text-slate-400 ml-auto">{u.role}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
